/**
 * The asset category every Nutanix product carries. Server lists request it so
 * Nutanix assets are returned at all. It does not identify what kind of Nutanix
 * product an asset is — use `isNutanixPrivateCloud` for that.
 */
export const NUTANIX_CATEGORY = 'Nutanix';

/**
 * The product codes for a Nutanix private cloud: the container a customer buys,
 * multi-tenant or single-tenant. The virtual machines inside one are
 * `Nutanix.MT.VM` and `Nutanix.ST.VM`, and they are ordinary server assets.
 */
export const NUTANIX_PRIVATE_CLOUD_TYPES = [
	'Nutanix.MT.VPC',
	'Nutanix.ST.Node',
];

/**
 * The product codes for a virtual machine running inside a Nutanix private
 * cloud. These are ordinary server assets: the customer manages them from the
 * portal, so they keep the standard presentation, detail page and actions. The
 * codes are named so `isPrismManaged` can exclude them, and so a private cloud
 * can ask `asset/list` for the machines that belong to it.
 */
export const NUTANIX_VM_TYPES = ['Nutanix.MT.VM', 'Nutanix.ST.VM'];

/**
 * Recognizes a virtual machine running inside a Nutanix private cloud. Drives
 * the one place a VM differs from the standard server presentation: the
 * monitoring page shows it no load view, because nothing collects stats inside
 * one yet. A VM keeps everything else a server asset gets.
 *
 * Unlike `isPrismManaged` this names the virtual machine codes, so an
 * unrecognized Nutanix product is not mistaken for a VM and keeps the load view
 * it reports a capability for.
 */
export const isNutanixVm = (type?: string | null) =>
	NUTANIX_VM_TYPES.includes(type || '');

/**
 * Recognizes a Nutanix private cloud from the product code `asset/details` and
 * `asset/list` return. Drives navigation and presentation: the detail page, the
 * server list row and the metadata a private cloud shows. A product code this
 * does not know is treated as a standard server, so a new Nutanix product keeps
 * its detail page and its data rather than losing them.
 *
 * This is deliberately separate from `isPrismManaged`. Recognition fails open so
 * an unknown product keeps its page; the action gate fails closed so it cannot
 * power itself off. See `isPrismManaged`.
 */
const isNutanixPrivateCloud = (type?: string | null) =>
	NUTANIX_PRIVATE_CLOUD_TYPES.includes(type || '');

/**
 * Recognizes any Nutanix asset the customer does not manage from the portal:
 * anything carrying the Nutanix category that is not one of the virtual machine
 * codes. Drives the destructive action gate and the Prism Central handoff.
 *
 * This keys on the category rather than the private cloud codes so an
 * unrecognized Nutanix container fails closed. `Nutanix.ST.Node.Man` is live in
 * Early Access and is covered here without being named, because it carries the
 * category and is not a virtual machine; it is left out of
 * `NUTANIX_PRIVATE_CLOUD_TYPES` because it is retired at GA and would only have
 * to be removed again.
 */
export const isPrismManaged = (
	type?: string | null,
	categories?: string[] | null,
) =>
	Boolean(categories?.includes(NUTANIX_CATEGORY)) &&
	!NUTANIX_VM_TYPES.includes(type || '');

export default isNutanixPrivateCloud;
